/**
 * Helpers de formatação para o Feed do Empreendedor.
 * - timer de expiração (vermelho < 2h, amarelo < 6h, "hoje às XXh", "em X dias")
 * - formatação do "há X tempo"
 */

export function expiraEmFormat(expira_em) {
  if (!expira_em) return { label: '', cor: 'text-gray-500', urgente: false };
  const now = new Date();
  const exp = new Date(expira_em);
  const ms = exp.getTime() - now.getTime();
  if (ms <= 0) return { label: 'Expirado', cor: 'text-gray-400', urgente: false };

  const horas = ms / (1000 * 60 * 60);

  if (horas < 2) {
    const min = Math.max(1, Math.round(ms / (1000 * 60)));
    return { label: `Expira em ${min} min`, cor: 'text-red-600 font-bold', urgente: true };
  }
  if (horas < 6) {
    return { label: `Expira em ${Math.round(horas)}h`, cor: 'text-amber-600 font-semibold', urgente: false };
  }
  if (horas < 24) {
    const hh = String(exp.getHours()).padStart(2, '0');
    return { label: `Expira hoje às ${hh}h`, cor: 'text-gray-700', urgente: false };
  }
  const dias = Math.ceil(horas / 24);
  return { label: `Expira em ${dias} ${dias === 1 ? 'dia' : 'dias'}`, cor: 'text-gray-500', urgente: false };
}

export function timeAgo(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  const ms = Date.now() - date.getTime();
  const seg = Math.floor(ms / 1000);
  if (seg < 60) return 'agora há pouco';
  const min = Math.floor(seg / 60);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'ontem';
  if (d < 7) return `há ${d} dias`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

/** Mapeia o chip de filtro do FeedPublico para os params da API.
 *
 * IMPORTANTE: usar os nomes CANÔNICOS V2 aqui. O backend expande automaticamente
 * para aliases V1 (ex.: filtrar "Alimentação & Bebidas" também casa posts
 * antigos com "Alimentos & Bebidas" via `_CAT_ALIASES`).
 */
export const FEED_CHIPS = [
  { key: 'todos', label: 'Todos', emoji: '🌎' },
  { key: 'alimentacao', label: 'Alimentação', emoji: '🍽️', negocio_categoria: 'Alimentação & Bebidas' },
  { key: 'beleza', label: 'Beleza', emoji: '✂️', negocio_categoria: 'Beleza & Estética' },
  { key: 'saude', label: 'Saúde', emoji: '🩺', negocio_categoria: 'Saúde' },
  { key: 'servicos', label: 'Serviços', emoji: '🔧', negocio_categoria: 'Serviços Pessoais & Gerais' },
  { key: 'compras', label: 'Compras', emoji: '🛍️', negocio_categoria: 'Compras & Varejo' },
  { key: 'educacao', label: 'Educação', emoji: '📚', negocio_categoria: 'Educação & Capacitação' },
  { key: 'producao-local', label: 'Produção Local', emoji: '🌿', negocio_categoria: 'Agronegócio & Produção Rural' },
  { key: 'construcao', label: 'Construção', emoji: '🔨', negocio_categoria: 'Construção, Reformas & Engenharia' },
  { key: 'pets', label: 'Pets', emoji: '🐾', negocio_categoria: 'Pets & Veterinária' },
  { key: 'promocoes', label: 'Promoções', emoji: '⚡', categoria_post: 'promocao-relampago' },
  { key: 'novidades', label: 'Novidades', emoji: '✨', categoria_post: 'novidade-geral' },
];

/**
 * Placeholders sugeridos por categoria_post no editor.
 * Categorias não listadas usam o genérico.
 */
export const PLACEHOLDERS_POST = {
  'prato-do-dia': 'Ex: Feijoada completa com arroz, farofa e couve. R$ 25. Até acabar!',
  'cardapio-especial': 'Ex: Cardápio especial de inverno: caldos, fondue e vinho.',
  'horario-disponivel': 'Ex: Tenho horário livre hoje às 14h e 16h.',
  'encaixe-disponivel': 'Ex: Tive um cancelamento, encaixo agora!',
  'profissional-disponivel': 'Ex: Profissional X disponível para atendimentos hoje.',
  'consulta-disponivel': 'Ex: Tenho 2 horários para consulta hoje à tarde.',
  'disponivel-hoje': 'Ex: Disponível hoje para serviços na região central.',
  'disponivel-regiao': 'Ex: Estou atendendo no bairro Jardim hoje.',
  'chegou-novidade': 'Ex: Chegou a coleção nova de verão!',
  'estoque-limitado': 'Ex: Restam apenas 5 unidades do item X.',
  'liquidacao-dia': 'Ex: Liquidação somente hoje: 30% off em tudo.',
  'novo-curso': 'Ex: Inscrições abertas para o curso de violão (Maio).',
  'aula-experimental': 'Ex: Aula experimental grátis na quinta às 19h.',
  'vaga-ultima-hora': 'Ex: Abriu uma vaga para a turma de pilates hoje.',
  'produto-disponivel': 'Ex: Chegou ovo caipira fresco da semana.',
  'colheita-semana': 'Ex: Colheita de hoje: alface, rúcula e tomate.',
  'horario-pet-disponivel': 'Ex: Tenho horário para banho hoje à tarde.',
  'disponivel-projeto': 'Ex: Disponível para projetos curtos esta semana.',
  'promocao-relampago': 'Ex: Por 6h: pizza grande 2 por 1!',
  'inauguracao': 'Ex: Abrimos nossas portas hoje! Venha nos conhecer.',
  'horario-especial': 'Ex: Hoje atendemos das 8h às 22h em horário estendido.',
  'novidade-geral': 'Ex: Estamos com uma novidade muito legal para vocês!',
  'evento-no-espaco': 'Ex: Show ao vivo neste sábado às 21h.',
  'turma-com-vagas': 'Ex: Turma de yoga abriu 3 vagas para hoje.',
  'novo-procedimento': 'Ex: Novo procedimento de massagem terapêutica.',
  'agenda-semana-aberta': 'Ex: Agenda da semana aberta — agende seu horário.',
};

export function placeholderFor(categoria_post) {
  return PLACEHOLDERS_POST[categoria_post] || 'Conte mais sobre sua disponibilidade...';
}
